/* eslint-disable no-unused-expressions */
import { useCallback, useEffect, useState, useRef } from 'react';
import { useMutation } from '@ts-core/lib/apollo';
import throttle from 'lodash/throttle';

import useStudentAuth from '../../account/hooks/useStudentAuth';

import { createMediaSessionMutation, updateMediaSessionProgressMutation, setMediaSessionAsSkippedMutation } from '../graphql/mutations';
import { readEntry, writeEntry, clearEntry, markEntrySent, isDirty, flushWithKeepalive } from '../utils/progressOutbox';

const DEFAULT_LOCAL_INTERVAL_MS = 5000;
// Default cadence: fire at most every 5% of the video, clamped to [20s, 120s].
// 5% means ~20 server writes per video regardless of length, which is the
// granularity completion-based analytics care about.
const DEFAULT_SERVER_RATIO = 0.05;
const DEFAULT_SERVER_MIN_MS = 20000;
const DEFAULT_SERVER_MAX_MS = 120000;

const computeServerInterval = (durationSec, ratio, minMs, maxMs) => {
  if (!durationSec || durationSec <= 0) return minMs;
  return Math.min(maxMs, Math.max(minMs, durationSec * 1000 * ratio));
};

export default function useMediaSession({ courseId, subjectId, mediaItemId }) {
  const [mediaSession, setMediaSession] = useState();
  const [knownDuration, setKnownDuration] = useState(0);
  const { userId } = useStudentAuth();

  const localIntervalMs = Number(import.meta.env.VITE_PROGRESS_LOCAL_INTERVAL_MS) || DEFAULT_LOCAL_INTERVAL_MS;
  const serverRatio = Number(import.meta.env.VITE_PROGRESS_SERVER_RATIO) || DEFAULT_SERVER_RATIO;
  const serverMinMs = Number(import.meta.env.VITE_PROGRESS_SERVER_MIN_MS) || DEFAULT_SERVER_MIN_MS;
  const serverMaxMs = Number(import.meta.env.VITE_PROGRESS_SERVER_MAX_MS) || DEFAULT_SERVER_MAX_MS;
  const serverIntervalMs = computeServerInterval(knownDuration, serverRatio, serverMinMs, serverMaxMs);

  const { _id: mediaSessionId } = mediaSession || {};

  // Latest snapshot kept on a ref so unload/visibility handlers (registered
  // once per session) can always reach the freshest values without
  // re-subscribing on every prop change.
  const latestRef = useRef({ userId, courseId, subjectId, mediaItemId, mediaSessionId });
  useEffect(() => {
    latestRef.current = { userId, courseId, subjectId, mediaItemId, mediaSessionId };
  }, [userId, courseId, subjectId, mediaItemId, mediaSessionId]);

  useEffect(() => {
    if (mediaSessionId) {
      setMediaSession();
    }
    setKnownDuration(0);
    return () => {
      // Switching to a different video without unmounting the page — make
      // sure the prior video's pending writes are flushed before we lose
      // the closure over its IDs.
      throttledLocalWrite.current?.flush();
      throttledServerSend.current?.flush();
      const { userId: uId, mediaItemId: mId } = latestRef.current;
      const entry = readEntry(uId, mId);
      if (isDirty(entry)) flushWithKeepalive(entry);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, subjectId, mediaItemId]);

  /**
   * Create media session
   */
  const [createMediaSession, { loading: creatingSession }] = useMutation(createMediaSessionMutation, {
    onCompleted: ({ mediaSession: mMediaSession }) => {
      setMediaSession(mMediaSession);
    },
  });
  const onCreateMediaSession = useCallback(
    (input) => {
      if (creatingSession || mediaSessionId) return;
      createMediaSession({
        variables: { courseId, subjectId, mediaItemId, input },
      });
    },
    [courseId, subjectId, mediaItemId, mediaSessionId, createMediaSession, creatingSession]
  );

  /**
   * Update media session progress (server-side)
   *
   * On ack, mark the outbox entry sent. If the server's recorded progress
   * has caught up to the locally-stored currentTime, drop the entry entirely.
   */
  const [updateMediaSessionProgress] = useMutation(updateMediaSessionProgressMutation, {
    onCompleted: ({ mediaSession: mMediaSession }) => {
      setMediaSession(mMediaSession);
      // Late-arriving completions for a video the user has since navigated
      // away from would otherwise mutate the wrong outbox entry — match on
      // the session id echoed back by the server.
      const { userId: uId, mediaItemId: mId } = latestRef.current;
      const entry = readEntry(uId, mId);
      if (!entry || entry.mediaSessionId !== mMediaSession?._id) return;
      if (mMediaSession.progress >= entry.currentTime) {
        clearEntry(uId, mId);
      } else {
        markEntrySent(uId, mId, Date.now());
      }
    },
  });
  const onUpdateMediaSessionProgress = useCallback(
    (input) => {
      if (!mediaSessionId) return;
      updateMediaSessionProgress({
        variables: { courseId, subjectId, mediaItemId, mediaSessionId, input },
      });
    },
    [updateMediaSessionProgress, courseId, subjectId, mediaItemId, mediaSessionId]
  );

  /**
   * Throttles: two cadences.
   *  - local: writes to localStorage outbox (frequent, cheap).
   *  - server: PUTs to the API (sparse).
   */
  const throttledLocalWrite = useRef();
  const throttledServerSend = useRef();

  useEffect(() => {
    const writeToOutbox = ({ duration, currentTime }) => {
      const { userId: uId, courseId: cId, subjectId: sId, mediaItemId: mId, mediaSessionId: msId } = latestRef.current;
      if (!msId) return;
      const existing = readEntry(uId, mId);
      writeEntry(uId, {
        courseId: cId,
        subjectId: sId,
        mediaItemId: mId,
        mediaSessionId: msId,
        currentTime,
        duration,
        updatedAt: Date.now(),
        sentAt: existing?.sentAt || 0,
      });
    };
    throttledLocalWrite.current = throttle(writeToOutbox, localIntervalMs, { leading: true, trailing: true });
    return () => throttledLocalWrite.current?.cancel();
  }, [localIntervalMs]);

  useEffect(() => {
    throttledServerSend.current = throttle(onUpdateMediaSessionProgress, serverIntervalMs, {
      leading: false,
      trailing: true,
    });
    return () => throttledServerSend.current?.cancel();
  }, [onUpdateMediaSessionProgress, serverIntervalMs]);

  /**
   * Set media session as skipped (unchanged behavior, but now also flushes
   * the server throttle immediately so a fast forward-seek lands promptly).
   */
  const [setMediaSessionAsSkipped] = useMutation(setMediaSessionAsSkippedMutation, {
    onCompleted: ({ mediaSession: mMediaSession }) => {
      setMediaSession(mMediaSession);
    },
  });
  const onSetMediaSessionAsSkipped = useCallback(
    (input) => {
      if (!mediaSessionId) return;
      setMediaSessionAsSkipped({
        variables: { courseId, subjectId, mediaItemId, mediaSessionId, input },
      });
    },
    [setMediaSessionAsSkipped, courseId, subjectId, mediaItemId, mediaSessionId]
  );
  const throttledSetMediaSessionAsSkipped = useRef();
  useEffect(() => {
    throttledSetMediaSessionAsSkipped.current = throttle(onSetMediaSessionAsSkipped, serverIntervalMs, {
      leading: true,
      trailing: true,
    });
    return () => throttledSetMediaSessionAsSkipped.current?.cancel();
  }, [onSetMediaSessionAsSkipped, serverIntervalMs]);

  /**
   * Recovery: when a new mediaSession arrives (created or refreshed), check
   * the outbox for a stored currentTime greater than the server's progress —
   * that indicates a previous browser-died write. Replay it.
   */
  useEffect(() => {
    if (!mediaSessionId) return;
    const entry = readEntry(userId, mediaItemId);
    if (!entry || !isDirty(entry)) return;
    if (entry.currentTime > (mediaSession?.progress || 0)) {
      onUpdateMediaSessionProgress({ duration: entry.duration, currentTime: entry.currentTime });
    } else {
      clearEntry(userId, mediaItemId);
    }
    // Only run on session id transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaSessionId]);

  /**
   * Lifecycle: flush on tab close / hide / unmount.
   *
   * `keepalive: true` is the right primitive here — sendBeacon can't reuse
   * the existing httpOnly auth cookie path cleanly, and Apollo's in-flight
   * mutation would be cancelled by the unload.
   */
  useEffect(() => {
    // Fire-and-forget on unload — we can't observe success here. Leave the
    // outbox entry dirty so the next mount's recovery path can replay (or
    // clear, once the server's recorded progress catches up).
    const flushKeepalive = () => {
      const { userId: uId, mediaItemId: mId } = latestRef.current;
      const entry = readEntry(uId, mId);
      if (!isDirty(entry)) return;
      flushWithKeepalive(entry);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushKeepalive();
    };
    window.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', flushKeepalive);
    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', flushKeepalive);
      throttledLocalWrite.current?.flush();
      throttledServerSend.current?.flush();
      flushKeepalive();
    };
  }, []);

  /**
   * Event handlers
   */
  const onMediaPlay = useCallback(
    (input) => {
      if (mediaSessionId) return;
      onCreateMediaSession(input);
    },
    [mediaSessionId, onCreateMediaSession]
  );

  const onMediaTimeUpdate = useCallback(
    ({ duration, currentTime }) => {
      const { _id, progress } = mediaSession || {};
      if (!_id || progress >= currentTime) return;
      // Latch the duration the first time we see a usable value so the
      // server cadence can adapt; subsequent equal updates are ignored by
      // the setter's identity check.
      if (duration && duration !== knownDuration) setKnownDuration(duration);
      throttledLocalWrite.current?.({ duration, currentTime });
      throttledServerSend.current?.({ duration, currentTime });
    },
    [mediaSession, knownDuration]
  );

  const onMediaSeeked = useCallback(
    ({ duration, currentTime }) => {
      const { _id, progress, skipped } = mediaSession || {};
      if (!_id || progress >= currentTime) return;
      if (!skipped) throttledSetMediaSessionAsSkipped.current?.({ duration, currentTime });
      // Push the latest progress out promptly rather than waiting for the
      // 60s window — seeks are user-intent signals.
      throttledLocalWrite.current?.({ duration, currentTime });
      throttledServerSend.current?.flush();
    },
    [mediaSession]
  );

  const onMediaEnded = useCallback(
    (input) => {
      if (!mediaSessionId) return;
      throttledLocalWrite.current?.cancel();
      throttledServerSend.current?.cancel();
      onUpdateMediaSessionProgress(input);
    },
    [onUpdateMediaSessionProgress, mediaSessionId]
  );

  return {
    onMediaPlay,
    onMediaTimeUpdate,
    onMediaSeeked,
    onMediaEnded,
  };
}
